/**
 * One-shot signup conversion marker.
 *
 * `/signup` sets sessionStorage before the AuthKit redirect. AuthRedirect also
 * sets a `radium_signup_pending` cookie on the API origin so the marker
 * survives the local Next :3088/:3000 → :8443 hop. After the first
 * authenticated app landing, AnalyticsTracker emits `sign_up` (and GTM
 * `signup_complete`) only if this tab still has the marker and wins
 * `ackSignupConversion`. `/login` clears
 * both stores so ordinary sign-ins never convert.
 */

export const SIGNUP_PENDING_STORAGE_KEY = "radium_signup_pending";
export const SIGNUP_PENDING_COOKIE = "radium_signup_pending";

const canUseSessionStorage = (): boolean =>
  typeof window !== "undefined" && typeof window.sessionStorage !== "undefined";

const readSignupPendingCookie = (): boolean => {
  if (typeof document === "undefined") {
    return false;
  }
  return document.cookie.split("; ").some((part) => part === `${SIGNUP_PENDING_COOKIE}=1`);
};

const writeSignupPendingCookie = (present: boolean): void => {
  if (typeof document === "undefined") {
    return;
  }
  const expires = present ? new Date(Date.now() + 60 * 60 * 1000).toUTCString() : "Thu, 01 Jan 1970 00:00:00 GMT";
  const assignment = present ? "1" : "";
  // Must include Secure on HTTPS so we overwrite the server-set Secure cookie.
  const secure = window.location.protocol === "https:" ? "; secure" : "";
  // Cookie Store API is not available in all supported browsers.
  // biome-ignore lint/suspicious/noDocumentCookie: first-party readable marker
  document.cookie = `${SIGNUP_PENDING_COOKIE}=${assignment}; path=/; expires=${expires}; samesite=lax${secure}`;
};

/** Mark that the user started the signup AuthKit redirect. */
export const markSignupStart = (): void => {
  if (canUseSessionStorage()) {
    try {
      window.sessionStorage.setItem(SIGNUP_PENDING_STORAGE_KEY, "1");
    } catch {
      // Ignore quota / private-mode failures; the redirect cookie may still fire.
    }
  }
  try {
    writeSignupPendingCookie(true);
  } catch {
    // Ignore cookie failures.
  }
};

/** Clear the signup marker without emitting a conversion event. */
export const clearSignupMarker = (): void => {
  if (canUseSessionStorage()) {
    try {
      window.sessionStorage.removeItem(SIGNUP_PENDING_STORAGE_KEY);
    } catch {
      // Ignore storage failures.
    }
  }
  try {
    writeSignupPendingCookie(false);
  } catch {
    // Ignore cookie failures.
  }
};

/** True when this tab still has a signup marker (sessionStorage or cookie). */
export const hasSignupMarker = (): boolean => {
  if (canUseSessionStorage()) {
    try {
      if (window.sessionStorage.getItem(SIGNUP_PENDING_STORAGE_KEY) === "1") {
        return true;
      }
    } catch {
      // Fall through to the cookie.
    }
  }
  try {
    return readSignupPendingCookie();
  } catch {
    return false;
  }
};
